// Einheitlicher Portfolio-Kalkulator — ersetzt duplizierte Berechnungen in
// AssetDetailScreen, AvgBuyCard und Dashboard.
// Basiert auf dem Lot-System (buildLots) als einzige Quelle der Wahrheit.
// Alle Finanzoperationen verwenden Decimal.js (precision 28).

import { matchTransfers } from './transferMatcher';
import { buildLots } from './lotSystem';
import { D, D_ZERO, D_EPSILON } from './decimalMath';
import { isStablecoin } from './stablecoinResolver';

const ONE_YEAR_MS = 365 * 86400000;

// Fiat-Währungen werden NICHT als Portfolio-Position angezeigt. Sie entstehen
// nur als Buchungsgröße (z.B. Binance Fiat Deposit → einzahlung EUR), sind
// aber kein Vermögenswert im Sinne des Krypto-Portfolios. Bilanzrundungen aus
// Trades, Fees und Withdrawals erzeugen sonst Phantom-EUR-Bestände im
// Dashboard, die der User nicht real besitzt.
// NFT: gleiches Pattern wie in WalletScreen/AssetDetailScreen — Asset-Recovery-
// und Airdrop-NFTs erscheinen mit coin='NFT' und sind kein fungibles Krypto-
// Asset (keine Marktdaten, kein Preis, kein Bestand im klassischen Sinn).
export const PORTFOLIO_FIAT_EXCLUDE = new Set([
  'EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL',
  'NFT',
]);

/**
 * Diagnostik-Hilfsfunktion: Vergleicht Lot-basierten Bestand mit einfacher
 * Laufbilanz. Gibt Abweichungen zurück.
 */
export function debugBalanceIssues(txs) {
  const pairs = matchTransfers(txs);
  const lots  = buildLots(txs, pairs);

  const run = {};
  const normalized = txs.map(tx => ({ ...tx }));
  for (const tx of normalized) {
    const add = (coin, amt) => {
      run[coin] = D(run[coin] || 0).plus(D(amt)).toNumber();
    };
    switch (tx.typ) {
      case 'kauf':       add(tx.coin,    tx.menge);           break;
      case 'verkauf':    add(tx.coin,   -tx.menge);           break;
      case 'swap':       add(tx.coinOut, -tx.mengeOut);
                         add(tx.coinIn,   tx.mengeIn);        break;
      case 'einzahlung': add(tx.coin,    tx.menge);           break;
      case 'auszahlung': add(tx.coin,   -tx.menge);           break;
      case 'burn':       add(tx.coin,   -tx.menge);           break;
      // futures_pnl / funding_fee: vorzeichenbehaftetes preis bestimmt Richtung
      case 'futures_pnl':
      case 'funding_fee':
        if (tx.coin && tx.menge > 0) {
          if (tx.preis < 0) add(tx.coin, -tx.menge);
          else if (tx.preis > 0) add(tx.coin,  tx.menge);
        }
        break;
      default:
        break;
    }
  }

  const matchedInIds  = new Set(pairs.map(p => p.in._docId  || String(p.in.id)));
  const matchedOutIds = new Set(pairs.map(p => p.out._docId || String(p.out.id)));

  const issues = [];
  const allCoins = new Set([...Object.keys(lots), ...Object.keys(run)]);

  for (const coin of allCoins) {
    const coinLots   = (lots[coin] || []).filter(l => !l.overConsumption);
    const lotBalance = coinLots.reduce((s, l) => D(s).plus(D(l.remainingAmount || 0)).toNumber(), 0);
    const runBalance = run[coin] || 0;
    const delta      = D(lotBalance).minus(D(runBalance)).toNumber();
    const maxBal     = Math.max(Math.abs(lotBalance), Math.abs(runBalance));
    const pctDelta   = maxBal > 1e-9 ? Math.abs(delta) / maxBal : 0;

    const unmatchedDeposits = txs.filter(
      tx => tx.typ === 'einzahlung' && tx.coin === coin && !matchedInIds.has(tx._docId || String(tx.id))
    );
    const unmatchedWithdrawals = txs.filter(
      tx => tx.typ === 'auszahlung' && tx.coin === coin && !matchedOutIds.has(tx._docId || String(tx.id))
    );

    if (unmatchedDeposits.length > 0) {
      issues.push({
        coin,
        lotBalance: D(lotBalance).toDecimalPlaces(8).toNumber(),
        runBalance: D(runBalance).toDecimalPlaces(8).toNumber(),
        delta:      D(delta).toDecimalPlaces(8).toNumber(),
        pctDelta:   Math.round(pctDelta * 10000) / 100,
        type: 'UNMATCHED_TRANSFER_IN',
        unmatchedDeposits: unmatchedDeposits.length,
      });
      continue;
    }

    if (Math.abs(delta) < 1e-6 && unmatchedWithdrawals.length === 0) continue;

    if (unmatchedWithdrawals.length > 0) {
      issues.push({
        coin,
        lotBalance: D(lotBalance).toDecimalPlaces(8).toNumber(),
        runBalance: D(runBalance).toDecimalPlaces(8).toNumber(),
        delta:      D(delta).toDecimalPlaces(8).toNumber(),
        pctDelta:   Math.round(pctDelta * 10000) / 100,
        type: 'UNMATCHED_TRANSFER_OUT',
        unmatchedWithdrawals: unmatchedWithdrawals.length,
      });
    } else {
      issues.push({
        coin,
        lotBalance: D(lotBalance).toDecimalPlaces(8).toNumber(),
        runBalance: D(runBalance).toDecimalPlaces(8).toNumber(),
        delta:      D(delta).toDecimalPlaces(8).toNumber(),
        pctDelta:   Math.round(pctDelta * 10000) / 100,
        type: delta > 0 ? 'EXCESS_LOT_BALANCE' : 'MISSING_LOT_BALANCE',
      });
    }
  }

  return issues;
}

/**
 * Berechnet das gesamte Portfolio aus allen Transaktionen.
 * Gibt ein Objekt { [sym]: AssetStats } zurück.
 */
export function calcPortfolio(txs, opts = {}) {
  // prebuiltFifo: { results, pool, transferPairs } aus buildFifoPool(txs).
  // Wenn vorhanden, sparen wir uns einen zweiten FIFO-Lauf (Dashboard ruft
  // calcPortfolio + buildReport zusammen auf).
  const lots = opts.prebuiltFifo
    ? buildLots(txs, null, { prebuiltFifo: opts.prebuiltFifo })
    : buildLots(txs, matchTransfers(txs));
  const result = {};

  for (const [sym, coinLots] of Object.entries(lots)) {
    if (PORTFOLIO_FIAT_EXCLUDE.has(sym)) continue;
    const active = coinLots.filter(l => l.remainingAmount > 1e-9);

    let menge        = D_ZERO;
    let invested     = D_ZERO;
    let mengePriced  = D_ZERO; // Menge der Lots mit bekanntem EK
    for (const l of active) {
      menge = menge.plus(D(l.remainingAmount));
      const hasPrice = !l.costBasisUnknown && D(l.originalBuyPrice).gt(D_ZERO);
      if (hasPrice) {
        invested    = invested.plus(D(l.remainingAmount).times(D(l.originalBuyPrice)));
        mengePriced = mengePriced.plus(D(l.remainingAmount));
      }
    }

    if (menge.gt(D_EPSILON)) {
      result[sym] = {
        sym,
        menge:       menge.toNumber(),
        invested:    invested.toNumber(),
        mengePriced: mengePriced.toNumber(),
        lots:        active,
      };
    }
  }
  return result;
}

/**
 * Berechnet Stats für ein einzelnes Asset.
 */
export function calcAssetStats(sym, txs) {
  const portfolio = calcPortfolio(txs);
  return portfolio[sym] ?? { sym, menge: 0, invested: 0, mengePriced: 0, lots: [] };
}

/**
 * Ø-Kaufpreis aus ALLEN Erwerbs-Transaktionen — unabhängig von FIFO-Konsumtion.
 *
 * Hintergrund: Der lot-basierte Ø in calcPortfolio reflektiert nur noch die
 * verbleibenden, nicht konsumierten Käufe. Bei häufigen Transfers zwischen
 * Börsen (auszahlung+einzahlung ohne Match) werden Kauf-Lots durch FIFO
 * verbraucht und der Ø-Preis fällt auf 0, obwohl der User reale Käufe getätigt
 * hat. Diese Funktion liefert ein stabiles, intuitives "was habe ich pro Coin
 * im Schnitt bezahlt"-Maß über alle priced acquisition tx.
 *
 * Berücksichtigt: kauf, swap (coinIn-Seite mit preis), einzahlung mit
 * preis/countervalue, income-typen (staking/airdrop/…) mit preis.
 *
 * @returns {number} Ø-Preis pro Coin, oder 0 wenn keine bepreisten Erwerbe.
 */
export function calcAcquisitionAvgPrice(sym, txs) {
  const target = String(sym).toUpperCase();
  let totalCost  = D_ZERO;
  let totalMenge = D_ZERO;

  for (const tx of txs) {
    const fee = D(tx.gebuehr || 0);

    if (tx.typ === 'kauf' && _coin(tx) === target) {
      const m = D(tx.menge || 0);
      const p = D(tx.preis || 0);
      if (m.gt(D_ZERO) && p.gt(D_ZERO)) {
        totalCost  = totalCost.plus(p).plus(fee);
        totalMenge = totalMenge.plus(m);
      }
    } else if (tx.typ === 'swap' && _coinIn(tx) === target) {
      const m = D(tx.mengeIn || 0);
      const p = D(tx.preis || 0);
      if (m.gt(D_ZERO) && p.gt(D_ZERO)) {
        totalCost  = totalCost.plus(p);
        totalMenge = totalMenge.plus(m);
      }
    } else if (tx.typ === 'einzahlung' && _coin(tx) === target) {
      const m = D(tx.menge || 0);
      const p = D(tx.preis || 0).gt(D_ZERO)
        ? D(tx.preis)
        : D(tx.countervalue || 0);
      if (m.gt(D_ZERO) && p.gt(D_ZERO)) {
        totalCost  = totalCost.plus(p).plus(fee);
        totalMenge = totalMenge.plus(m);
      }
    } else if (
      (tx.typ === 'staking' || tx.typ === 'airdrop' || tx.typ === 'mining' ||
       tx.typ === 'cashback' || tx.typ === 'interest') &&
      _coin(tx) === target
    ) {
      const m = D(tx.menge || 0);
      const p = D(tx.preis || 0);
      if (m.gt(D_ZERO) && p.gt(D_ZERO)) {
        totalCost  = totalCost.plus(p).plus(fee);
        totalMenge = totalMenge.plus(m);
      }
    }
  }

  return totalMenge.gt(D_EPSILON) ? totalCost.div(totalMenge).toNumber() : 0;
}

/**
 * Investment-Total-Berechnung — robust gegen Lot-Pool-Verzerrungen.
 * Liefert "wieviel hast du insgesamt eingezahlt vs. was wurde davon schon
 * realisiert" — exakt die User-Intuition.
 *
 * Vorteil ggü. stats.invested aus calcPortfolio: hängt nicht vom Lot-Pool-
 * Zustand ab (der bei ungematchten Transfers verzerrt wird), sondern rechnet
 * direkt aus TX-Historie + FIFO-Realisierungen.
 *
 * @returns {{ totalInvested: number, realizedCosts: number, remainingInvested: number }}
 *   totalInvested:      Σ aller Käufe in EUR (was hast du eingezahlt)
 *   realizedCosts:      Σ Anschaffungskosten der durch Verkauf/Swap-Out
 *                       bereits realisierten Coins (FIFO-konsumiert)
 *   remainingInvested:  totalInvested - realizedCosts (Anschaffung des Bestands)
 */
export function calcAssetInvestment(sym, txs, fifoResults = null) {
  const target = String(sym).toUpperCase();
  let totalInvested = D_ZERO;

  // Total Investment: alle erwerbsbringenden TXs mit erfasstem EUR-preis
  for (const tx of txs) {
    const fee = D(tx.gebuehr || 0);

    if (tx.typ === 'kauf' && _coin(tx) === target) {
      const p = D(tx.preis || 0);
      if (p.gt(D_ZERO)) totalInvested = totalInvested.plus(p).plus(fee);
    } else if (tx.typ === 'swap' && _coinIn(tx) === target) {
      const p = D(tx.preis || 0);
      if (p.gt(D_ZERO)) totalInvested = totalInvested.plus(p);
    } else if (tx.typ === 'einzahlung' && _coin(tx) === target) {
      const p = D(tx.preis || 0).gt(D_ZERO) ? D(tx.preis) : D(tx.countervalue || 0);
      if (p.gt(D_ZERO)) totalInvested = totalInvested.plus(p).plus(fee);
    } else if (
      (tx.typ === 'staking' || tx.typ === 'airdrop' || tx.typ === 'mining' ||
       tx.typ === 'cashback' || tx.typ === 'interest') &&
      _coin(tx) === target
    ) {
      const p = D(tx.preis || 0);
      if (p.gt(D_ZERO)) totalInvested = totalInvested.plus(p).plus(fee);
    }
  }

  // Realized Costs: FIFO-Anschaffungskosten der bereits konsumierten Coins
  // (aus fifo()-Result oder lazy importiert)
  let realizedCosts = D_ZERO;
  if (fifoResults && Array.isArray(fifoResults)) {
    for (const ev of fifoResults) {
      const sellsCoin =
        (ev.typ === 'verkauf'     && _coin(ev)    === target) ||
        (ev.typ === 'swap'        && _coinOut(ev) === target) ||
        (ev.typ === 'burn'        && _coin(ev)    === target) ||
        (ev.typ === 'loss'        && _coin(ev)    === target) ||
        ((ev.typ === 'futures_pnl' || ev.typ === 'funding_fee') && _coin(ev) === target);
      if (sellsCoin && Number.isFinite(Number(ev.ak))) {
        realizedCosts = realizedCosts.plus(D(ev.ak));
      }
    }
  }

  const remainingInvested = totalInvested.minus(realizedCosts);
  return {
    totalInvested:     totalInvested.toDecimalPlaces(2).toNumber(),
    realizedCosts:     realizedCosts.toDecimalPlaces(2).toNumber(),
    // Floor bei 0 — bei Daten-Lücken kann realizedCosts > totalInvested erscheinen
    remainingInvested: Math.max(0, remainingInvested.toDecimalPlaces(2).toNumber()),
  };
}

/**
 * Cash-In: ECHTE Fiat-Einzahlung in das Asset.
 * Im Gegensatz zu calcAssetInvestment.totalInvested (welches alle Erwerbs-TXs
 * mit erfasstem EUR-Wert summiert, inkl. Income und Crypto→Crypto-Swaps), zählt
 * hier NUR was real an Fiat geflossen ist:
 *
 *   1. Direkte Fiat-Käufe des Assets (tx.typ='kauf' mit preis > 0)
 *   2. Indirekte Käufe via Stablecoin-Brücke (Stablecoin → Asset-Swap, z. B.
 *      EUR→USDC→XLM auf Binance Spot — die USDC-Brücke gilt als Einzahlung)
 *
 * NICHT gezählt:
 *   - Crypto→Crypto-Swaps (XLM → BTC: nur Re-Allocation, kein neues Geld)
 *   - Income (Staking/Airdrop/Mining: Einkommen, keine Einzahlung)
 *   - Ungematchte Einzahlungen (Wallet-Transfers ohne EUR-Wert)
 *
 * Stufe 1: Stablecoin-Quelle wird NICHT getraced. Bei Round-Trip-Szenarien
 * (XLM→USDC→XLM) könnte cashIn leicht überzeichnet sein. Stablecoin-Source-
 * Tracing wäre Stufe-2-Task falls beim User sichtbar problematisch.
 *
 * @returns {number} EUR-Summe der echten Einzahlungen in das Asset
 */
// Helper: robustes Coin-Lookup. Manche TXs haben `coin`, manche `sym`
// (je nach Import-Quelle / Edit-Pfad) — beide müssen unterstützt werden.
function _coin(tx)    { return String(tx.coin    ?? tx.sym    ?? '').toUpperCase(); }
function _coinIn(tx)  { return String(tx.coinIn  ?? tx.symIn  ?? '').toUpperCase(); }
function _coinOut(tx) { return String(tx.coinOut ?? tx.symOut ?? '').toUpperCase(); }

export function calcAssetCashIn(sym, txs) {
  const target = String(sym).toUpperCase();
  let cashIn = D_ZERO;

  for (const tx of txs) {
    // 1. Direkter Fiat-Kauf
    if (tx.typ === 'kauf' && _coin(tx) === target) {
      const p = D(tx.preis || 0);
      if (p.gt(D_ZERO)) {
        cashIn = cashIn.plus(p).plus(D(tx.gebuehr || 0));
      }
    }
    // 2. Stablecoin-Brücke: Stablecoin → Asset
    if (tx.typ === 'swap' && _coinIn(tx) === target) {
      const coinOutSym = _coinOut(tx);
      if (isStablecoin(coinOutSym)) {
        const p = D(tx.preis || 0);
        cashIn = cashIn.plus(p.gt(D_ZERO) ? p : D(tx.mengeOut || 0));
      }
    }
  }

  return cashIn.toDecimalPlaces(2).toNumber();
}

/**
 * Cash-Out: was du an EUR durch Verkäufe rausgenommen hast (gegenstück zu cashIn).
 * Zählt:
 *   1. Direkte EUR-Verkäufe des Assets (typ='verkauf' mit preis > 0)
 *   2. Stablecoin-Brücken-OUT (Swap Asset → Stablecoin, der Stablecoin-Wert als
 *      effektiver "Cash-Out" — du bist raus aus dem Asset zu Fiat-equivalent)
 * Plus: zählt die Anzahl der Cash-Out-Events für die UI-Anzeige ("aus N Verkäufen").
 *
 * @returns {{ cashOut: number, count: number }}
 */
export function calcAssetCashOut(sym, txs) {
  const target = String(sym).toUpperCase();
  let cashOut = D_ZERO;
  let count = 0;
  for (const tx of txs) {
    // 1. Direkter Fiat-Verkauf
    if (tx.typ === 'verkauf' && _coin(tx) === target) {
      const p = D(tx.preis || 0);
      if (p.gt(D_ZERO)) {
        cashOut = cashOut.plus(p);
        count += 1;
      }
    }
    // 2. Stablecoin-Brücke OUT: Asset → Stablecoin
    if (tx.typ === 'swap' && _coinOut(tx) === target) {
      const coinInSym = _coinIn(tx);
      if (isStablecoin(coinInSym)) {
        const p = D(tx.preis || 0);
        cashOut = cashOut.plus(p.gt(D_ZERO) ? p : D(tx.mengeIn || 0));
        count += 1;
      }
    }
  }
  return { cashOut: cashOut.toDecimalPlaces(2).toNumber(), count };
}

/**
 * Realisierter Gewinn pro Asset: Σ ev.gv aus FIFO-Realisierungen.
 * Filtert auf Events die diesen Coin verbraucht haben (verkauf/swap-out/loss/burn).
 */
export function calcRealizedGain(sym, fifoResults) {
  if (!Array.isArray(fifoResults)) return 0;
  const target = String(sym).toUpperCase();
  let sum = D_ZERO;
  for (const ev of fifoResults) {
    const sellsCoin =
      (ev.typ === 'verkauf'     && _coin(ev)    === target) ||
      (ev.typ === 'swap'        && _coinOut(ev) === target) ||
      (ev.typ === 'burn'        && _coin(ev)    === target) ||
      (ev.typ === 'loss'        && _coin(ev)    === target) ||
      // futures_pnl / funding_fee: realisiertes PnL auf dem Margin-Coin (meist USDT)
      ((ev.typ === 'futures_pnl' || ev.typ === 'funding_fee') && _coin(ev) === target);
    if (sellsCoin && Number.isFinite(Number(ev.gv))) {
      sum = sum.plus(D(ev.gv));
    }
  }
  return sum.toDecimalPlaces(2).toNumber();
}

/**
 * Berechnet Haltefrist-Informationen (für DE Steuerrecht).
 * Haltedauer in ganzen Tagen (Math.floor) — kein Floating-Point-Grenzfall.
 */
export function calcHaltefrist(sym, txs) {
  const stats = calcAssetStats(sym, txs);
  const today = new Date();

  const activeLots = stats.lots.map(lot => {
    const ageMs = today - new Date(lot.originalBuyDate);
    const isOld = Math.floor(ageMs / 86400000) >= 365;
    const buyDateUnknown = !!(lot.costBasisUnknown && !isOld);
    return { ...lot, taxFree: isOld, buyDateUnknown };
  });

  let taxFreeMenge = D_ZERO;
  let taxableMenge = D_ZERO;
  let unknownMenge = D_ZERO;
  let nextFreeDays = null;

  for (const lot of activeLots) {
    const amt = D(lot.remainingAmount);
    if (lot.taxFree) {
      taxFreeMenge = taxFreeMenge.plus(amt);
    } else if (lot.buyDateUnknown) {
      unknownMenge = unknownMenge.plus(amt);
    } else {
      taxableMenge = taxableMenge.plus(amt);
      const daysLeft = Math.ceil(
        (ONE_YEAR_MS - (today - new Date(lot.originalBuyDate))) / 86400000
      );
      if (nextFreeDays === null || daysLeft < nextFreeDays) nextFreeDays = daysLeft;
    }
  }

  const totalMenge = D(stats.menge);
  const pctFree    = totalMenge.gt(D_ZERO)
    ? taxFreeMenge.div(totalMenge).times(100).toNumber()
    : 0;

  return {
    taxFreeMenge:         taxFreeMenge.toNumber(),
    taxableMenge:         taxableMenge.toNumber(),
    unknownMenge:         unknownMenge.toNumber(),
    nextFreeDays,
    pctFree,
    activeLots,
    totalMenge:           totalMenge.toNumber(),
    hasUnmatchedTransfers: unknownMenge.gt(D_EPSILON),
  };
}
